'use client';

import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { getCurrency } from '@/app/currency';
import { NotesCardContent } from '@entities/notes-card';

import { useViewMutation, useGetNoteQuery } from '@shared/api';
import { ENV } from '@shared/config';
import type { Locale } from '@shared/libs';

import { NotesCardQueryData, Notes, Musician, Questions, Reviews, ReviewRatings } from '@shared/types';
import type { FaqItem } from '@shared/utils';

type Props = {
  data: NotesCardQueryData['data'];
  otherArrangement: Notes[];
  musiciansArrangement: Notes[];
  similarMusicians: Musician[];
  questions: Questions;
  questionsPager: { page_current: number; pages_all: number; records_all: number; records_per_page: number };
  reviews: Reviews;
  reviewsPager?: { page_current: number; pages_all: number; records_all: number; records_per_page: number };
  reviewRatings: ReviewRatings;
  locale: Locale;
  breadcrumb: {
    title: string;
    url: string;
  }[];
  h1: string;
  path: string;
  urlMusician: string | null;
  urlTitle: string | null;
  urlArrangement: string | null;
  faqItems: FaqItem[];
};

export const NotesCard = ({
  data: serverData,
  locale,
  otherArrangement,
  musiciansArrangement,
  similarMusicians,
  questions,
  questionsPager,
  reviews,
  reviewsPager,
  reviewRatings,
  breadcrumb,
  h1,
  path,
  urlMusician,
  urlTitle,
  urlArrangement,
  faqItems,
}: Props) => {
  const viewMutation = useViewMutation();
  const { data } = useGetNoteQuery({
    urlMusician,
    urlTitle,
    urlArrangement,
  });

  const truncateText = (text: string): string => {
    if (!text) {
      return text;
    }

    // Удаляем ведущие пробелы и переносы строк
    const trimmedText = text.trim();

    if (!trimmedText) {
      return '';
    }

    // Находим первую непустую строку
    const lines = trimmedText.split('\n');

    const firstNonEmptyLine = lines.find(line => line.trim());

    if (firstNonEmptyLine) {
      const cleanLine = firstNonEmptyLine.trim();

      // Проверяем есть ли еще содержательные строки после первой
      const hasMoreContent = lines.slice(lines.indexOf(firstNonEmptyLine) + 1).some(line => line.trim());

      // Если строка слишком длинная, обрезаем до 30 символов
      if (cleanLine.length > 30) {
        return `${cleanLine.substring(0, 30)}...`;
      }

      // Если есть еще содержательные строки, добавляем многоточие
      if (hasMoreContent) {
        return `${cleanLine}...`;
      }

      return cleanLine;
    }

    return '';
  };

  // Всегда применяем обрезание к финальным данным
  const rawData = data || serverData;


  const initialData = rawData ? {
    ...rawData,
    text: truncateText(rawData.text || '')
  } : rawData;


  useEffect(() => {
    viewMutation.mutate({ offer_id: serverData?.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void (async () => {
      // eslint-disable-next-line @typescript-eslint/await-thenable
      const currency = await getCurrency();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      if (window.dataLayer) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        window.dataLayer.push({
          ecommerce: {
            currencyCode: currency?.value || ENV.DEFAULT_CERRENCY,
            detail: {
              products: [
                {
                  id: initialData.id.toString(),
                  name: `${initialData.musicians?.[0]?.title} - ${initialData.title} - ${initialData.arrangement.title}`,
                  category: initialData?.instruments?.[0]?.title,
                  brand: initialData?.arrangement?.title,
                  price: initialData.price.MIDI.VALUE,
                },
              ],
            },
          },
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ErrorBoundary 
      fallback={null}
      onError={(error, errorInfo) => {
        console.error('Критическая ошибка в NotesCardContent:', {
          error: error.message,
          stack: error.stack,
          errorInfo,
          noteId: initialData?.id,
          questionsCount: questions?.length || 0,
          reviewsCount: reviews?.length || 0,
        });
        
        // Отправляем ошибку в систему логирования
        if (typeof window !== 'undefined') {
          try {
            void fetch('/api-v2/send-log', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                level: 3,
                msg: JSON.stringify({
                  error: 'Критическая ошибка рендера страницы ноты',
                  details: error.message,
                  noteId: initialData?.id,
                  stack: error.stack,
                  componentStack: errorInfo.componentStack,
                  questionsCount: questions?.length || 0,
                  reviewsCount: reviews?.length || 0,
                }),
                href: window.location.href,
                titlePage: document?.title,
                userAgent: navigator?.userAgent,
              }),
            });
          } catch (logError) {
            console.error('Ошибка отправки лога об ошибке NotesCardContent:', logError);
          }
        }
      }}
    >
      <NotesCardContent
        data={initialData}
        breadcrumb={breadcrumb}
        locale={locale}
        otherArrangement={otherArrangement}
        musiciansArrangement={musiciansArrangement}
        similarMusicians={similarMusicians}
        questions={questions}
        questionsPager={questionsPager}
        reviews={reviews}
        reviewsPager={reviewsPager}
        reviewRatings={reviewRatings}
        h1={h1}
        path={path}
        faqItems={faqItems}
      />
    </ErrorBoundary>
  );
};
